import { isAxiosError } from "axios";
import { useParams, usePathname } from "next/navigation";
import { useCallback, useEffect, useRef } from "react";

import { useUserContext } from "@/context/user.context";

export const NewRelicConfig = () => {
  const pathname = usePathname();
  const params = useParams<{ id?: string }>();
  const { user } = useUserContext();
  const generalConfigCompleteRef = useRef(false);
  const userConfigCompleteRef = useRef(false);

  const configureUser = useCallback(() => {
    if (user && !userConfigCompleteRef.current && window.newrelic) {
      userConfigCompleteRef.current = true;

      // Prefix custom attributes with `UPWG_` to allow easily seeing the full
      // list of custom attributes in NR UI.
      window.newrelic.setCustomAttribute("UPWG_user_id", user.id);
      window.newrelic.setCustomAttribute("UPWG_is_internal", user.isInternal);

      return true;
    }

    return false;
  }, [user]);

  useEffect(() => {
    if (!user || userConfigCompleteRef.current) {
      return;
    }

    if (configureUser()) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (configureUser()) {
        window.clearInterval(intervalId);
      }
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [configureUser, user]);

  const configureGeneral = useCallback(() => {
    if (!generalConfigCompleteRef.current && window.newrelic) {
      generalConfigCompleteRef.current = true;

      // Here we can tell New Relic to ignore specific errors that it would
      // otherwise capture. This should be used highly specifically, and only
      // for errors that are intentionally expected to avoid accidental loss
      // of important metrics or insight into potential incidents.
      //
      // Each ignored error should include a comment explaining why the error
      // should be ignored.
      // Returning `true` informs New Relic that it should ignore the error.
      window.newrelic.setErrorHandler((error) => {
        // Avoid sending 404's of screener builder requests on the locations
        // page. This page handles both legacy and builder screeners. Legacy
        // screeners are expected to 404 as they have no builder info. This,
        // in conjunction with retries, can lead to tremendous spikes of 404's
        // that don't actually indicate a problem. There is an edge case in
        // which an actual builder 404's unexpectedly on locations, but in that
        // instance we will receive the 404 from the default screener locations
        // endpoint
        if (
          // 404's in pages other than locations should be reported as usual
          params.id &&
          pathname === `/screener-builder/${params.id}/locations`
        ) {
          const isBuilder404Regex = new RegExp(
            /^Screener Builder with screener ID .+ not found$/,
          );

          return (
            isAxiosError(error) &&
            error.status === 404 &&
            error.response?.data?.message &&
            isBuilder404Regex.test(error.response?.data?.message ?? "")
          );
        }

        // If none of the above conditions are met, fall back to the default
        // New Relic behavior (error is captured and sent to the dashboard)
        return false;
      });

      return true;
    }

    return false;
  }, [params.id, pathname]);

  useEffect(() => {
    if (generalConfigCompleteRef.current || configureGeneral()) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (configureGeneral()) {
        window.clearInterval(intervalId);
      }
    }, 500);

    return () => window.clearInterval(intervalId);
  }, [configureGeneral]);

  return null;
};
